import React from 'react';
import styled from 'styled-components';
import ScheduleItem from './ScheduleItem';

const CellWrapper = styled.td`
    text-align: center;
    vertical-align: middle;
    padding: 4px 2px;
    height: 40px;
    min-height: 40px;
    position: relative;
    cursor: ${props => props.canManage ? 'pointer' : 'default'};
    background-color: ${props => {
        // 選取狀態優先級最高
        if (props.isSelected) return '#e3f2fd';
        if (props.isToday) return '#e8f4f8';
        if (props.isWeekend) return '#fafafa';
        return 'transparent';
    }};
    border: ${props => props.isSelected ? '2px solid #2196f3' : '1px solid #ddd'};
    border-radius: ${props => props.isSelected ? '4px' : '0px'};
    
    &:hover {
        background-color: ${props => {
            // 選取狀態優先級最高
            if (props.isSelected) return '#e3f2fd';
            return props.canManage ? '#f0f0f0' : 'inherit';
        }};
    }
    
    // 選取狀態優先於週末和今天樣式
    &.selected {
        background-color: #e3f2fd !important;
        border: 2px solid #2196f3 !important;
        border-radius: 4px !important;
    }
    
    &.weekend:not(.selected) {
        background-color: #fafafa;
    }
    
    &.today:not(.selected) {
        background-color: #e8f4f8;
    }
`;

const ScheduleItemsContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 3px;
    height: 100%;
    flex-wrap: wrap;
`;

const ScheduleDot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    cursor: ${props => props.canManage ? 'move' : 'pointer'};
    transition: all 0.2s ease;
    flex-shrink: 0;
    background-color: ${props => props.color || '#f0f0f0'};
    
    &:hover {
        transform: ${props => props.canManage ? 'scale(1.2)' : 'scale(1.1)'};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        border-color: rgba(0, 0, 0, 0.3);
    }
`;

const ScheduleTableCell = ({
    date,
    schedules,
    canManage,
    getColorForType,
    scheduleTypes,
    onDateClick,
    onDateDoubleClick,
    onDragOver,
    onDrop,
    onDragStart,
    onRemoveSchedule,
    isWeekend,
    isToday,
    userUuid,
    isSelected,
    isSelectionMode
}) => {
    const handleClick = () => {
        if (canManage && onDateClick) {
            onDateClick(date);
        }
    };

    const handleDoubleClick = (e) => {
        // 在選取模式下，阻止雙擊事件觸發
        if (isSelectionMode) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (canManage && onDateDoubleClick) {
            e.stopPropagation();
            // 如果有 userUuid，則傳遞給 onDateDoubleClick
            if (userUuid) {
                onDateDoubleClick(date, userUuid);
            } else {
                onDateDoubleClick(date);
            }
        }
    };

    const handleDragOver = (e) => {
        if (canManage && onDragOver) {
            onDragOver(e);
        }
    };

    const handleDrop = (e) => {
        if (canManage && onDrop) {
            onDrop(e, date);
        }
    };

    // 根據日期類型決定顏色
    const getDotColor = (schedule) => {
        const dayType = schedule.day_type || 'normal';
        if (dayType === 'rest_day') {
            return '#ff9800'; // 休息日：橙色
        } else if (dayType === 'regular_holiday') {
            return '#f44336'; // 例假日：紅色
        }
        return getColorForType(schedule.type_id);
    };

    // 取得 tooltip 文字
    const getTooltipText = (schedule) => {
        const scheduleType = schedule.type_name || '未知';
        const dayType = schedule.day_type || 'normal';
        const timeRange = schedule.time_range || '';
        
        let dayTypeLabel = '';
        if (dayType === 'rest_day') {
            dayTypeLabel = ' [休息日]';
        } else if (dayType === 'regular_holiday') {
            dayTypeLabel = ' [例假日]';
        }
        if (schedule.is_national_holiday) {
            dayTypeLabel += ' [國定假日]';
        }
        
        return scheduleType + dayTypeLabel + (timeRange ? ' (' + timeRange + ')' : '');
    };

    return (
        <CellWrapper
            className={`schedule-cell ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
            canManage={canManage}
            isWeekend={isWeekend}
            isToday={isToday}
            isSelected={isSelected}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="gridcell"
        >
            {schedules.length > 0 && (
                <ScheduleItemsContainer>
                    {schedules.map((schedule, index) => (
                        <ScheduleDot
                            key={schedule.id || index}
                            color={getDotColor(schedule)}
                            canManage={canManage}
                            draggable={canManage}
                            onDragStart={(e) => {
                                e.stopPropagation();
                                if (canManage && onDragStart) {
                                    onDragStart(e, schedule, date);
                                }
                            }}
                            title={getTooltipText(schedule)}
                            onClick={(e) => {
                                if (canManage && onRemoveSchedule) {
                                    e.stopPropagation();
                                    // 可以添加右鍵選單或點擊刪除功能
                                }
                            }}
                        />
                    ))}
                </ScheduleItemsContainer>
            )}
        </CellWrapper>
    );
};

export default ScheduleTableCell;
